import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Gelijk aan media-breakpoint-down(sm) en media-breakpoint-up(md) uit $grid-breakpoints
const MOBILE_QUERY = '(max-width: 767.98px)';
const DESKTOP_QUERY = '(min-width: 768px)';

// Mobiel: de sticky knop neemt het over zodra deze knop uit beeld is gescrolled
const CTA_SELECTOR = '.m-hero-programme__visuals-button--inline';

// Desktop: de sticky knop bevriest op de plek die deze knop bij de video heeft
const FLOATING_SELECTOR = '.m-hero-programme__visuals-button--floating';

// Ruimte onder de knop als zijn eigen plek te laag ligt voor de viewport
const FREEZE_MARGIN = 24;

// Waar de sticky knop weer moet verdwijnen: bij de gele footerbanner, of bij de
// footer zelf als die banner er niet is (querySelector pakt de eerste in de DOM)
const END_SELECTOR = '.c-footer-banner-wrapper, .c-footer';

export default class HeroProgramme {
	constructor(elm) {
		this.elm = elm;
		this.sticky = this.elm.querySelector('[data-hero-programme-sticky]');

		if (!this.sticky) {
			return;
		}

		const container = this.elm.closest('[data-barba="container"]');

		// ScrollSmoother zet een transform op #smooth-content, waardoor position: fixed
		// daarbinnen aan die wrapper hangt in plaats van aan de viewport. Vandaar dat de
		// knop naar .c-fixed-viewport verhuist, die buiten de smoother staat.
		const viewport = container ? container.querySelector('.c-fixed-viewport') : null;

		if (viewport) {
			viewport.appendChild(this.sticky);
		}

		this.cta = this.elm.querySelector(CTA_SELECTOR) || this.elm;
		this.floating = this.elm.querySelector(FLOATING_SELECTOR);
		this.end = (container || document).querySelector(END_SELECTOR);

		// matchMedia maakt de trigger opnieuw aan bij het passeren van het breakpoint
		// en draait de cleanup terug bij resize
		this.mm = gsap.matchMedia();

		// Mobiel: de knop komt onderin beeld zodra de inline knop weg is gescrolled
		this.mm.add(MOBILE_QUERY, () =>
			this.create({
				trigger: this.cta,
				start: 'bottom top',
			})
		);

		// Desktop: de knop blijft hangen op de plek die hij bij de video heeft. Zonder
		// visuals is er geen knop bij de video en dus ook niets om te bevriezen.
		if (this.floating) {
			this.mm.add(DESKTOP_QUERY, () => {
				this.place();

				return this.create({
					trigger: this.floating,
					start: () => `top ${this.freeze()}px`,
					// Bij een resize verschuift ook de bevroren plek zelf
					onRefresh: (self) => {
						this.place();
						this.sync(self);
					},
				});
			});
		}
	}

	// Beide breakpoints gebruiken dezelfde trigger, alleen het startpunt verschilt
	create(vars) {
		const trigger = ScrollTrigger.create({
			endTrigger: this.end || this.elm,
			end: this.end ? 'top bottom' : 'max',
			onToggle: (self) => this.toggle(self.isActive),
			// Na een resize zijn start/end herberekend, dus de stand opnieuw bepalen
			onRefresh: (self) => this.sync(self),
			...vars,
		});

		// onToggle vuurt niet bij het aanmaken, dus de beginstand zetten we zelf
		this.sync(trigger);

		return () => this.toggle(false);
	}

	// Op welke hoogte in beeld de knop op desktop blijft hangen: zijn eigen plek bij
	// scrollpositie 0, of net boven de onderkant van het scherm als die plek op een
	// lage viewport onder de vouw ligt
	freeze() {
		const lowest = window.innerHeight - this.floating.offsetHeight - FREEZE_MARGIN;

		return Math.max(0, Math.min(this.layoutTop(this.floating), lowest));
	}

	// offsetTop negeert de transform die ScrollSmoother op #smooth-content zet, dus
	// de som van de keten is de plek van de knop bij scrollpositie 0
	layoutTop(elm) {
		let top = 0;
		let node = elm;

		while (node) {
			top += node.offsetTop;
			node = node.offsetParent;
		}

		return top;
	}

	// De sticky knop hangt in .c-fixed-viewport, dus dit zijn schermcoördinaten. De
	// horizontale plek komt van de knop bij de video, zodat de vw-marges uit de scss
	// hier niet herhaald hoeven te worden.
	place() {
		const { left } = this.floating.getBoundingClientRect();

		this.sticky.style.setProperty('--hero-sticky-left', `${Math.round(left)}px`);
		this.sticky.style.setProperty('--hero-sticky-top', `${Math.round(this.freeze())}px`);
	}

	// Leidt de stand af uit de actuele scrollpositie in plaats van uit isActive, die
	// nog niet gezet is vlak na het aanmaken van de trigger
	sync(trigger) {
		const scroll = trigger.scroll();

		this.toggle(scroll >= trigger.start && scroll < trigger.end);
	}

	toggle(visible) {
		// Expliciet naar een boolean: classList.toggle() negeert een tweede argument
		// dat undefined is en flipt de class dan alsnog
		const isVisible = visible === true;

		this.sticky.classList.toggle('is-visible', isVisible);

		// Op desktop verschijnt de sticky knop precies op de plek van de knop bij de
		// video, dus die maakt op hetzelfde moment plaats
		this.elm.classList.toggle('is-sticky-visible', isVisible);
	}

	destroy() {
		if (this.mm) {
			this.mm.revert();
			this.mm = null;
		}

		// De knop staat buiten de barba-container, dus hier zelf opruimen
		if (this.sticky) {
			this.sticky.remove();
			this.sticky = null;
		}
	}
}
